import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface AuthorityCondition { text: string; kind?: string; status?: string }
export interface AuthorityTermination { text: string; occurred?: boolean | null; occurred_on?: string | null }

/** One per-grant binding authority. Mirrors berserkr.fetch_case_authorities. */
export interface Authority {
  id: string;
  instrument_type: string; // trust | will | contract | license | permit | ...
  title: string;
  grantor: string | null;
  grantee: string | null;
  scope: string;
  scope_tags: string[];
  effective_date: string | null;
  expiration_date: string | null;
  validity_basis: "date_range" | "conditional" | "perpetual" | "until_event" | string;
  conditions: AuthorityCondition[];
  termination_triggers: AuthorityTermination[];
  status: "in_force" | "not_yet_effective" | "expired" | "terminated" | "condition_unmet" | "suspended" | "unknown" | string;
  confidence: string;
  source_label: string | null;
  source_locator: string | null;
  verbatim_quote: string | null;
  quote_chunk_id: string | null;
  quote_verified: boolean;
}

/** The Shield Wall's authorities for a case. */
export function useCaseAuthorities(caseId: string | undefined) {
  return useQuery<Authority[], Error>({
    queryKey: ["case-authorities", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      if (!caseId) return [];
      const { data, error } = await berserkr().rpc("fetch_case_authorities", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as Authority[]) ?? [];
    },
  });
}
